/**
 * The save vocabulary and the operation key (P4-T2; `UX-FLOWS.md` §7).
 *
 * Four outcomes are named and kept distinct, because collapsing them is how a user is told
 * the wrong thing:
 *
 *   - **saved** — the write was accepted *and* the view was refreshed from the server.
 *   - **saved but refresh failed** — the write was accepted, the follow-up read was not. The
 *     money moved; the screen is stale. Re-submitting here would record it twice, so this
 *     state never invites a resubmission.
 *   - **not saved** — a refusal that means nothing was written (a `422`, a stale `If-Match`,
 *     an intent conflict). The draft is intact and the same intent may be corrected.
 *   - **outcome unknown** — the request was sent and no answer arrived, so whether it landed
 *     is genuinely unknown. The intent is retained and may only be *reconciled*: retried
 *     with the **same** `Idempotency-Key`, which the API turns into a replay of the original
 *     result rather than a second write.
 *
 * The operation key is minted once per intent. A retry of the same intent reuses the key; a
 * different intent gets a new one. That is the whole of "same-intent retries preserve the
 * operation key", and it is a pure function here so it can be asserted without a server.
 */

export type SaveState = 'idle' | 'saving' | 'saved' | 'saved-refresh-failed' | 'not-saved' | 'unknown';

export interface PendingIntent {
  readonly operation: string;
  readonly targetId: string | null;
  readonly body: unknown;
  readonly idempotencyKey: string;
}

/** A UUID `Idempotency-Key`; the API refuses a financial write without one. */
export function newOperationKey(): string {
  return globalThis.crypto.randomUUID();
}

/** A key-sorted JSON rendering, so the same intent always produces the same signature. */
function canonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, item]) => item !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${canonical(item)}`).join(',')}}`;
}

/** What makes two attempts "the same intent": the operation, its target and the fields sent. */
export function intentSignature(operation: string, targetId: string | null, body: unknown): string {
  return `${operation}\n${targetId ?? ''}\n${canonical(body)}`;
}

/**
 * The `Idempotency-Key` this attempt must carry.
 *
 * `reused` is the honest answer to "did this retry preserve the operation key?" — the UI
 * says so on screen, and the browser journey asserts it from the request headers.
 */
export function keyForIntent(
  pending: PendingIntent | null,
  signature: string,
  mint: () => string = newOperationKey,
): { readonly key: string; readonly reused: boolean } {
  if (
    pending !== null &&
    intentSignature(pending.operation, pending.targetId, pending.body) === signature
  ) {
    return { key: pending.idempotencyKey, reused: true };
  }
  return { key: mint(), reused: false };
}

/** What a financial write's outcome means, before the follow-up read is considered. */
export type WriteKind = 'accepted' | 'refused' | 'unknown';

/**
 * Classify a write by the status it returned.
 *
 * The split is not "4xx bad / 5xx worse": it is "can this attempt have written anything?".
 * Every status the contract names as a refusal (`400`, `401`, `403`, `404`, `409`, `412`,
 * `422`, `428`, `429`) is answered before any effect, so nothing was written. A `5xx`, a
 * `0` (no answer) and anything unrecognised leave that question open, and are `unknown`
 * rather than pretended into a refusal.
 */
export function classifyWrite(status: number | null): WriteKind {
  if (status === null) return 'unknown';
  if (status >= 200 && status < 300) return 'accepted';
  if ([400, 401, 403, 404, 409, 412, 422, 428, 429].includes(status)) return 'refused';
  return 'unknown';
}

/** The named state a write and its follow-up read produce. */
export function stateAfterWrite(kind: WriteKind, refreshOk: boolean): SaveState {
  if (kind === 'refused') return 'not-saved';
  if (kind === 'unknown') return 'unknown';
  return refreshOk ? 'saved' : 'saved-refresh-failed';
}

/**
 * Whether this state may offer the save action again.
 *
 * Only `not-saved` may: nothing happened, so the corrected intent is a fresh attempt.
 * `saved-refresh-failed` must not (the money moved), and `unknown` must not either — it may
 * only reconcile the retained intent with its original key.
 */
export function invitesResave(state: SaveState): boolean {
  return state === 'not-saved';
}

/** Whether this state means "resolve the retained intent before starting another". */
export function needsReconciliation(state: SaveState): boolean {
  return state === 'unknown';
}

/**
 * The sentence the user reads. One line per state, and the three failure lines are
 * deliberately different from each other.
 */
export function describeSaveState(state: SaveState): string {
  switch (state) {
    case 'idle':
      return '';
    case 'saving':
      return 'Saving…';
    case 'saved':
      return 'Saved.';
    case 'saved-refresh-failed':
      return 'Saved, but this screen could not be refreshed. Do not submit it again — reload to see the recorded entry.';
    case 'not-saved':
      return 'Not saved. Nothing was recorded; your draft is intact.';
    case 'unknown':
      return 'Outcome unknown: the request was sent and no answer arrived. Reconcile this same intent — do not create it again.';
  }
}

/** The write attempt the UI holds while a save is in flight. */
export interface SaveAttempt {
  readonly pending: PendingIntent | null;
  readonly state: SaveState;
  readonly message: string;
}
